import { App, Form, Input, InputNumber, Select } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { BsDashCircle } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomInput,
  CustomModal,
  CustomSelect,
  CustomSharedDatePicker,
  CustomSwitch,
  CustomTypography,
} from "sipli-fleet-core/components";
import ModalStepHeader from "src/components/ModalStepHeader/ModalStepHeader";
import {
  useMutationInstallUnit,
  useQueryGetUnitManagementAdjusterLookup,
  useQueryGetUnitManagementClientLookup,
  useQueryGetUnitManagementVehicleMakeLookup,
  useQueryGetUnitManagementVehicleModelLookup,
  useQueryGetUnitManagementVehicleTypeLookup,
  useQuerySearchUnitByImeiOrSerial,
} from "src/queries/unitManagement.queries";
import { QueryNames } from "src/queries/queries.constant";
import { useQueryClient } from "@tanstack/react-query";
import {
  extractLookupItems,
  getFieldValue,
  getQueryData,
  getUnitLabel,
  MIN_SEARCH_LENGTH,
  numberOrUndefined,
  toOptions,
  toUnitOptions,
} from "src/container/DeviceManagement/shared/unitManagementUtils";
import type { UnitManagementLookupItem } from "src/services/unitManagementService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { AddFuelSensorModal } from "../components/DeviceManagementModals";

interface InstallUnitModalDevice {
  id: string | number;
  imei?: string | null;
  serial?: string | null;
}

interface InstallUnitModalProps {
  open: boolean;
  device: InstallUnitModalDevice | null;
  onClose: () => void;
  onInstalled?: () => void;
}

interface InstallUnitFormValues {
  licensePlate?: string;
  vin?: string;
  vehicleMakeId?: string | number;
  vehicleModelId?: string | number;
  vehicleTypeId?: string | number;
  odometer?: number;
  motorHours?: number;
  firstRegistrationDate?: Dayjs;
  vehicleNotes?: string;
  adjusterId?: string | number;
  customerId?: string | number;
  deviceId?: string | number;
  withTpms?: boolean;
  unitNotes?: string;
}

const VEHICLE_STEP_FIELDS: (keyof InstallUnitFormValues)[] = [
  "licensePlate",
  "vin",
  "vehicleMakeId",
  "vehicleModelId",
  "vehicleTypeId",
];

const UNIT_STEP_FIELDS: (keyof InstallUnitFormValues)[] = ["adjusterId", "customerId", "deviceId"];

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;

  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return undefined;

  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== "object") return undefined;

  const message = (data as { message?: unknown }).message;
  return typeof message === "string" ? message : undefined;
};

const InstallUnitModal = ({ open, device, onClose, onInstalled }: InstallUnitModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<InstallUnitFormValues>();
  const selectedVehicleMakeId = Form.useWatch("vehicleMakeId", form);
  const { selectedDistributor } = useSelectDistributor();
  const distributorId = selectedDistributor ?? undefined;
  const [step, setStep] = useState(0);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedDevice, setSelectedDevice] = useState<UnitManagementLookupItem | null>(null);
  const [fuelSensorSearch, setFuelSensorSearch] = useState("");
  const [fuelSensorModalOpen, setFuelSensorModalOpen] = useState(false);
  const [selectedFuelSensorId, setSelectedFuelSensorId] = useState<string | number | undefined>();
  const [fuelSensors, setFuelSensors] = useState<UnitManagementLookupItem[]>([]);

  const { data: vehicleMakeData, isLoading: isVehicleMakeLoading } =
    useQueryGetUnitManagementVehicleMakeLookup({}, { enabled: open });
  const { data: vehicleModelData, isLoading: isVehicleModelLoading } =
    useQueryGetUnitManagementVehicleModelLookup(
      selectedVehicleMakeId,
      {},
      { enabled: open && !!selectedVehicleMakeId }
    );
  const { data: vehicleTypeData, isLoading: isVehicleTypeLoading } =
    useQueryGetUnitManagementVehicleTypeLookup({}, { enabled: open });
  const { data: adjusterData, isLoading: isAdjusterLoading } =
    useQueryGetUnitManagementAdjusterLookup(distributorId ? { distributorId } : {}, {
      enabled: open && !!distributorId,
    });
  const { data: clientData, isLoading: isClientLoading } = useQueryGetUnitManagementClientLookup(
    distributorId ? { distributorId } : {},
    { enabled: open && !!distributorId }
  );
  const { data: deviceSearchData, isLoading: isDeviceSearchLoading } =
    useQuerySearchUnitByImeiOrSerial(
      { query: deviceSearch, unitType: "DVC", distributorId },
      { enabled: open }
    );
  const { data: fuelSensorSearchData, isLoading: isFuelSensorSearchLoading } =
    useQuerySearchUnitByImeiOrSerial(
      { query: fuelSensorSearch, unitType: "FUESNSR", distributorId },
      { enabled: open }
    );
  const { mutateAsync: installUnit, isPending: isInstalling } = useMutationInstallUnit();

  const vehicleMakeOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleMakeData))),
    [vehicleMakeData]
  );
  const vehicleModelOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleModelData))),
    [vehicleModelData]
  );
  const vehicleTypeOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleTypeData))),
    [vehicleTypeData]
  );
  const adjusterOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(adjusterData))),
    [adjusterData]
  );
  const clientOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(clientData))),
    [clientData]
  );
  const deviceItems = useMemo(
    () => extractLookupItems(getQueryData(deviceSearchData)),
    [deviceSearchData]
  );
  const deviceOptions = useMemo(() => {
    const searchOptions = toUnitOptions(deviceItems);
    if (!device || searchOptions.some((option) => option.value === device.id)) {
      return searchOptions;
    }

    const label = getUnitLabel({
      id: device.id,
      imei: device.imei ?? undefined,
      serial: device.serial ?? undefined,
    });
    return [{ value: device.id, label }, ...searchOptions];
  }, [deviceItems, device]);
  const fuelSensorItems = useMemo(
    () => extractLookupItems(getQueryData(fuelSensorSearchData)),
    [fuelSensorSearchData]
  );
  const fuelSensorOptions = useMemo(() => toUnitOptions(fuelSensorItems), [fuelSensorItems]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setStep(0);
      setDeviceSearch("");
      setSelectedDevice(null);
      setFuelSensors([]);
      setFuelSensorSearch("");
      setFuelSensorModalOpen(false);
      setSelectedFuelSensorId(undefined);
      return;
    }

    if (device) {
      form.setFieldValue("deviceId", device.id);
      setSelectedDevice({
        id: device.id,
        imei: device.imei ?? undefined,
        serial: device.serial ?? undefined,
      });
    }
  }, [open, device, form]);

  const handleAddFuelSensor = (value: string | number) => {
    const sensor = fuelSensorItems.find((item) => getFieldValue(item) === value);
    if (!sensor) return;

    setFuelSensors((prevSensors) => {
      if (prevSensors.some((item) => getFieldValue(item) === value)) {
        return prevSensors;
      }

      return [...prevSensors, sensor];
    });
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
    setFuelSensorSearch("");
  };

  const handleConfirmAddFuelSensor = () => {
    if (selectedFuelSensorId === undefined) return;
    handleAddFuelSensor(selectedFuelSensorId);
  };

  const handleCloseFuelSensorModal = () => {
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
    setFuelSensorSearch("");
  };

  const handleRemoveFuelSensor = (value: string | number | undefined) => {
    setFuelSensors((prevSensors) => prevSensors.filter((item) => getFieldValue(item) !== value));
  };

  const handleClose = () => {
    form.resetFields();
    setStep(0);
    setDeviceSearch("");
    setSelectedDevice(null);
    setFuelSensors([]);
    setFuelSensorSearch("");
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
    onClose();
  };

  const handleNext = async () => {
    try {
      await form.validateFields(VEHICLE_STEP_FIELDS);
      setStep(1);
    } catch {
      // validation errors are already shown on the fields
    }
  };

  const handleSave = async () => {
    try {
            await form.validateFields(UNIT_STEP_FIELDS);
      const allValues = form.getFieldsValue(true);
      const deviceId = selectedDevice?.id ?? allValues.deviceId;

      const payload = {
        vehicleDetail: {
          adjusterId: allValues.adjusterId,
          clientId: allValues.customerId,
          modelId: allValues.vehicleModelId,
          odometer: numberOrUndefined(allValues.odometer),
          vin: allValues.vin,
          registrationDate: allValues.firstRegistrationDate?.toISOString(),
          motorHours: numberOrUndefined(allValues.motorHours),
          vehicleTypeId: allValues.vehicleTypeId,
          licensePlate: allValues.licensePlate,
          notes: allValues.vehicleNotes,
          active: true,
        },
        deviceDetail: {
          id: deviceId,
          deviceId,
          notes: allValues.unitNotes,
          ...(distributorId ? { distributorId } : {}),
          withTpms: Boolean(allValues.withTpms),
        },
        fuelTankAndUnit: fuelSensors
          .map((sensor) => getFieldValue(sensor))
          .filter((value): value is string | number => value !== undefined)
          .map((fuelSensorUnitId) => ({ fuelSensorUnitId })),
        insurance: [],
        accessories: [],
        consumableResources: [],
      };

      await installUnit(payload);
      message.success(t("unit_installed_successfully"));
      queryClient.invalidateQueries({ queryKey: QueryNames.warehouseUnitList });
      onInstalled?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        message.error(getErrorMessage(error) ?? t("unit_install_failed"));
      }
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        onCancel={handleClose}
        okText={step === 0 ? t("next") : t("save")}
        cancelText={t("cancel")}
        onOk={step === 0 ? handleNext : handleSave}
        okBtnLoading={isInstalling}
        width={835}
        destroyOnHidden
        closable={false}
      >
      <ModalStepHeader
        steps={[t("vehicle_details"), t("unit_details")]}
        current={step}
        onClose={handleClose}
        closeLabel={t("cancel")}
      />

      <CustomTypography.CustomTitle className="mb-8! text-xl! font-bold!">
        {t("install_unit")}
      </CustomTypography.CustomTitle>

      <div className="mb-3.5 flex flex-col gap-2">
        <CustomTypography.CustomText className="block text-lg! leading-5.5! font-bold! text-black!">
          {step === 0 ? t("vehicle_details") : t("unit_details")}
        </CustomTypography.CustomText>
        <CustomTypography.CustomText className="block text-sm! leading-4.5! font-normal! text-gray9!">
          {step === 0
            ? t("vehicle_details_install_description")
            : t("unit_details_install_description")}
        </CustomTypography.CustomText>
      </div>

      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ withTpms: false }}>
        {step === 0 ? (
          <div className="grid gap-x-3 md:grid-cols-2">
            <Form.Item
              name="licensePlate"
              label={`${t("license_plate")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomInput placeholder={t("type")} maxLength={10} />
            </Form.Item>
            <Form.Item
              name="vin"
              label={`${t("vin")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomInput placeholder={t("type")} maxLength={25} />
            </Form.Item>
            <Form.Item
              name="vehicleMakeId"
              label={`${t("vehicle_brand")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                placeholder={t("select")}
                options={vehicleMakeOptions}
                loading={isVehicleMakeLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="vehicleModelId"
              label={`${t("vehicle_model")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                placeholder={t("select")}
                options={vehicleModelOptions}
                loading={isVehicleModelLoading}
                disabled={!selectedVehicleMakeId}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="vehicleTypeId"
              label={`${t("vehicle_type")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                placeholder={t("select")}
                options={vehicleTypeOptions}
                loading={isVehicleTypeLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="odometer" label={t("odometer_gps")}>
              <InputNumber min={0} className="w-full!" placeholder={t("type")} />
            </Form.Item>
            <Form.Item name="motorHours" label={t("motor_hours")}>
              <InputNumber min={0} className="w-full!" placeholder={t("type")} />
            </Form.Item>
            <Form.Item name="firstRegistrationDate" label={t("first_registration_date")}>
              <CustomSharedDatePicker placeholder={t("select_date")} />
            </Form.Item>
            <Form.Item name="vehicleNotes" label={t("notes")} className="md:col-span-2">
              <Input.TextArea placeholder={t("written_text")} className="min-h-18!" />
            </Form.Item>
          </div>
        ) : (
          <div>
            <div className="grid gap-x-3 md:grid-cols-2">
              <Form.Item
                name="adjusterId"
                label={`${t("adjuster")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={adjusterOptions}
                  loading={isAdjusterLoading}
                  disabled={!distributorId}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="customerId"
                label={`${t("customer")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={clientOptions}
                  loading={isClientLoading}
                  disabled={!distributorId}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </div>

            <Form.Item
              name="deviceId"
              label={`${t("device")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={t("search_by_imei_or_serial")}
                options={deviceOptions}
                loading={isDeviceSearchLoading}
                disabled={!distributorId}
                className="h-8!"
                onSearch={setDeviceSearch}
                onSelect={(value) => {
                  const found = deviceItems.find((item) => getFieldValue(item) === value);
                  setSelectedDevice(found ?? null);
                }}
                onClear={() => setSelectedDevice(null)}
                notFoundContent={
                  deviceSearch.trim().length < MIN_SEARCH_LENGTH
                    ? t("type_at_least_3_characters")
                    : t("no_data")
                }
              />
            </Form.Item>

            <Form.Item className="mb-3!">
              <div className="flex items-center gap-2">
                <Form.Item name="withTpms" valuePropName="checked" noStyle>
                  <CustomSwitch />
                </Form.Item>
                <CustomTypography.CustomText>{t("add_tpms")}</CustomTypography.CustomText>
              </div>
            </Form.Item>

            <Form.Item name="unitNotes" label={t("notes")}>
              <Input.TextArea placeholder={t("written_text")} className="min-h-18!" />
            </Form.Item>

            <div className="flex flex-col gap-3 bg-gray1 p-4 mt-6">
              <div className="flex min-h-6 items-center justify-between gap-3">
                <CustomTypography.CustomText className="text-lg! font-bold! text-black!">
                  {t("fuel_sensors")}
                </CustomTypography.CustomText>
                <CustomButton
                  type="primary"
                  buttonSize="small"
                  label={t("add_fuel_sensor")}
                  disabled={!distributorId}
                  className="text-white!"
                  onClick={() => setFuelSensorModalOpen(true)}
                />
              </div>

              <div className="flex flex-col gap-2">
                {fuelSensors.length > 0 ? (
                  fuelSensors.map((sensor) => {
                    const value = getFieldValue(sensor);

                    return (
                      <div
                        key={String(value)}
                        className="flex min-h-6 items-center justify-between gap-3 bg-white px-2 py-1 text-sm text-black"
                      >
                        <span>
                          {t("associate_unit")}: {getUnitLabel(sensor)}
                        </span>
                        <button
                          type="button"
                          className="flex h-4.5 w-4.5 items-center justify-center text-primary"
                          onClick={() => handleRemoveFuelSensor(value)}
                          aria-label="Remove fuel sensor"
                        >
                          <BsDashCircle />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <CustomTypography.CustomText className="text-sm! text-gray7!">
                    {t("no_fuel_sensors_added")}
                  </CustomTypography.CustomText>
                )}
              </div>
            </div>
          </div>
        )}
        </Form>
      </CustomModal>

      <AddFuelSensorModal
        open={fuelSensorModalOpen}
        options={fuelSensorOptions}
        loading={isFuelSensorSearchLoading}
        value={selectedFuelSensorId}
        onSearch={setFuelSensorSearch}
        onChange={setSelectedFuelSensorId}
        onCancel={handleCloseFuelSensorModal}
        onConfirm={handleConfirmAddFuelSensor}
        notFoundContent={
          fuelSensorSearch.trim().length < MIN_SEARCH_LENGTH
            ? t("type_at_least_3_characters")
            : t("no_data")
        }
      />
    </>
  );
};

export default InstallUnitModal;
